import { useUnit } from 'effector-react'
import {
  Button,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Typography,
} from 'antd'
import {
  MAP_TEMPLATE_KEYS,
  MAP_TEMPLATES,
  OPTIONS_TEMPLATE,
} from 'src/pages/gateway/edit/atoms/const'
import { addNoticeFX } from 'src/pages/gateway/edit/model'
import { Gateway } from 'src/pages/gateway/edit/types'

type Props = {
  gateway: Gateway
  onClose: () => void
}

type FormValues = {
  typeNotice: 'template' | 'custom'
  template: keyof typeof MAP_TEMPLATES
  notice: string
}

export const AddNoticeModal = ({ gateway, onClose }: Props) => {
  const [form] = Form.useForm()
  const { Item } = Form
  const { Text } = Typography
  const { TextArea } = Input
  const { Group } = Radio

  const loading = useUnit(addNoticeFX.pending)
  const type = Form.useWatch<string>('typeNotice', form)
  const numberTemplate = Form.useWatch<string>('template', form)

  const handleSubmit = async ({ typeNotice, template, notice }: FormValues) => {
    const message =
      typeNotice === 'template'
        ? JSON.stringify({ key: MAP_TEMPLATE_KEYS[template] })
        : notice

    try {
      await addNoticeFX({
        url: `/gateway/${gateway.gatewayId}/enable-client-notification`,
        body: { message },
      })
      handleCancel()
    } catch (error) {
      console.error(error)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      title={
        <Text style={{ fontSize: 20 }}>
          Activate Gateway{' '}
          <strong style={{ color: 'var(--color-primary)' }}>
            {gateway.internalName}
          </strong>{' '}
          Notification
        </Text>
      }
      centered
      wrapClassName="ant_modal"
      width={700}
      maskClosable={false}
      open={Boolean(gateway)}
      onCancel={handleCancel}
      footer={null}
    >
      <Divider />
      <Form layout="vertical" onFinish={handleSubmit} form={form}>
        <Item
          name="typeNotice"
          required={false}
          label={
            <Text style={{ fontSize: 16 }} strong>
              Type Message
            </Text>
          }
          rules={[
            {
              required: true,
              message: 'Required field',
            },
          ]}
          tooltip="Standard templates are automatically translated into the customer's language. Custom messages are displayed as entered and are not translated."
        >
          <Group
            options={[
              {
                value: 'template',
                label: 'Standard Template',
              },
              { value: 'custom', label: 'Custom Message' },
            ]}
          />
        </Item>
        <Flex vertical style={{ minHeight: 200 }}>
          {type === 'template' && (
            <>
              <Item
                required={false}
                name="template"
                style={{ width: 180 }}
                label={
                  <Text style={{ fontSize: 16 }} strong>
                    Template
                  </Text>
                }
                rules={[
                  {
                    required: true,
                    message: 'Required field',
                  },
                ]}
              >
                <Select options={OPTIONS_TEMPLATE} />
              </Item>
              {numberTemplate && (
                <div
                  style={{
                    padding: 12,
                    border: '1px solid var(--crm-border)',
                    borderRadius: 6,
                    background: 'var(--crm-bg-elevated)',
                  }}
                >
                  {MAP_TEMPLATES[numberTemplate as keyof typeof MAP_TEMPLATES]}
                </div>
              )}
            </>
          )}
          {type === 'custom' && (
            <Item
              required={false}
              name="notice"
              label={
                <Text style={{ fontSize: 16 }} strong>
                  Message
                </Text>
              }
              rules={[
                {
                  required: true,
                  transform: (value) => value?.trim(),
                  message: 'Required field',
                },
              ]}
            >
              <TextArea rows={6} autoFocus />
            </Item>
          )}
        </Flex>
        <Flex gap={8} justify="flex-end">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            danger
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={loading}
          >
            Add Notice
          </Button>
        </Flex>
      </Form>
    </Modal>
  )
}
